import { LoggerService } from "@/shared/services";

import { GeoBounds } from "./geoBounds";

/**
 * Efficient coordinate storage for a single map path.
 * Uses typed arrays for zero-copy transfer from worker and fast binary search.
 */
export class MapPathData {
  private _latitudes: Float64Array = new Float64Array(0);
  private _longitudes: Float64Array = new Float64Array(0);
  private _logTimes: BigUint64Array = new BigUint64Array(0);
  private _isComplete = false;
  // Each extreme starts at the opposite infinity so the first coordinate
  // replaces it. An infinity that survives means no finite coordinate has been
  // appended on that axis, which is the condition getBounds reports as
  // "no bounds yet".
  private _minLat = Infinity;
  private _maxLat = -Infinity;
  private _minLon = Infinity;
  private _maxLon = -Infinity;

  /** Current length (number of points loaded so far) */
  get length(): number {
    return this._latitudes.length;
  }

  /** Whether all data has been received */
  get isComplete(): boolean {
    return this._isComplete;
  }

  /**
   * Append a chunk of coordinates received from the worker. The three arrays
   * are parallel (index i of each describes one point), so they must be the
   * same length; a mismatch throws rather than silently misaligning every
   * point past the shortest array's end.
   */
  appendChunk(
    latitudes: Float64Array,
    longitudes: Float64Array,
    logTimes: BigUint64Array,
  ): void {
    if (
      latitudes.length !== longitudes.length ||
      latitudes.length !== logTimes.length
    ) {
      throw new Error(
        `Coordinate chunk arrays must be the same length: got ${latitudes.length} latitudes, ${longitudes.length} longitudes, and ${logTimes.length} log times`,
      );
    }

    // TODO: this recopies every point received so far on each chunk, so
    // loading a path costs time quadratic in its chunk count. At the worker's
    // 1000-point chunks a 100k-point path copies about 5 million elements per
    // array, 15 million across the three, where one pass would copy 100k
    // each. Tracking allocated capacity separately from the filled count and
    // doubling it on overflow would make the copying amortized linear;
    // `length` and every accessor below would then read the filled count
    // instead of the array length.
    const newLat = new Float64Array(this._latitudes.length + latitudes.length);
    newLat.set(this._latitudes, 0);
    newLat.set(latitudes, this._latitudes.length);
    this._latitudes = newLat;

    const newLon = new Float64Array(
      this._longitudes.length + longitudes.length,
    );
    newLon.set(this._longitudes, 0);
    newLon.set(longitudes, this._longitudes.length);
    this._longitudes = newLon;

    const newTime = new BigUint64Array(this._logTimes.length + logTimes.length);
    newTime.set(this._logTimes, 0);
    newTime.set(logTimes, this._logTimes.length);
    this._logTimes = newTime;

    // Fold the chunk into the running bounds. A NaN coordinate fails every
    // comparison below, so it is skipped instead of poisoning an extreme.
    for (let i = 0; i < latitudes.length; i++) {
      const lat = latitudes[i];
      const lon = longitudes[i];
      if (lat < this._minLat) {
        this._minLat = lat;
      }
      if (lat > this._maxLat) {
        this._maxLat = lat;
      }
      if (lon < this._minLon) {
        this._minLon = lon;
      }
      if (lon > this._maxLon) {
        this._maxLon = lon;
      }
    }
  }

  /**
   * Geographic extent of the coordinates appended so far, or undefined until a
   * finite coordinate has been appended on each axis. Longitude extremes are a
   * plain numeric min and max, so a path crossing the antimeridian yields
   * bounds that span the long way around the globe.
   */
  getBounds(): GeoBounds | undefined {
    if (!Number.isFinite(this._minLat) || !Number.isFinite(this._minLon)) {
      return undefined;
    }
    return {
      minLat: this._minLat,
      maxLat: this._maxLat,
      minLon: this._minLon,
      maxLon: this._maxLon,
    };
  }

  /** Mark loading as complete */
  markComplete(totalCount: number): void {
    this._isComplete = true;
    // Verify the count matches
    if (this._latitudes.length !== totalCount) {
      LoggerService.warn(
        `MapPathData: Expected ${totalCount} points, but have ${this._latitudes.length}`,
      );
    }
  }

  /** Get coordinate at index */
  getCoordinate(
    index: number,
  ): { lat: number; lon: number; logTime: bigint } | undefined {
    if (index < 0 || index >= this._latitudes.length) {
      return undefined;
    }
    return {
      lat: this._latitudes[index],
      lon: this._longitudes[index],
      logTime: this._logTimes[index],
    };
  }

  /** Binary search for nearest point by logTime (for playback) */
  findNearestIndex(targetLogTime: bigint): number {
    if (this._logTimes.length === 0) {
      return 0;
    }

    let left = 0;
    let right = this._logTimes.length - 1;

    // Handle edge cases
    if (targetLogTime <= this._logTimes[0]) {
      return 0;
    }
    if (targetLogTime >= this._logTimes[right]) {
      return right;
    }

    // Binary search for the closest index
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const midTime = this._logTimes[mid];

      if (midTime === targetLogTime) {
        return mid;
      }

      if (midTime < targetLogTime) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    // At this point, right < left
    // right is the largest index with time < targetLogTime
    // left is the smallest index with time > targetLogTime
    // Return the closer one
    if (left >= this._logTimes.length) {
      return right;
    }

    const leftDiff =
      this._logTimes[left] > targetLogTime
        ? this._logTimes[left] - targetLogTime
        : targetLogTime - this._logTimes[left];
    const rightDiff =
      this._logTimes[right] > targetLogTime
        ? this._logTimes[right] - targetLogTime
        : targetLogTime - this._logTimes[right];

    return leftDiff < rightDiff ? left : right;
  }

  /** Get all coordinates as GeoJSON-compatible array (for Mapbox LineString) */
  toGeoJSONCoordinates(): [number, number][] {
    const coords: [number, number][] = [];
    for (let i = 0; i < this._latitudes.length; i++) {
      // GeoJSON uses [lon, lat] order
      coords.push([this._longitudes[i], this._latitudes[i]]);
    }
    return coords;
  }
}
